// LayoutMain – hlavní rozcestník: Nastavení, Jedna stanice, Skupina (+ admin sekce) + poslední 4 stanice z localStorage
// Přihlášení: UserIcon v pravém horním rohu. Admin ikona se zobrazí po přihlášení s is_admin = 'Y'.
// Viditelné ikony menu řídí useDeployConfigStore.menuIcons (server, podle role a varianty
// nasazení) — viz doc/plans/deploy-variants-a-b-c.md D2. Admin sekce zůstává řízená jen
// isAdmin, napříč variantami.
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getLastStations } from '../hooks/useLastStations'
import { getLastMeteogramPoints } from '../hooks/useLastMeteogramPoints'
import { getLastStationChartsStations } from '../hooks/useLastStationChartsStations'
import { getLastForecastSelection } from '../hooks/useLastForecastSelection'
import { UserIcon } from '../components/auth/UserIcon'
import { useAuthStore } from '../store/useAuthStore'
import { useDeployConfigStore } from '../store/useDeployConfigStore'
import { useSavedStationGroups } from '../hooks/useStationGroups'
import { clearStationCache, clearCodebookCache, clearGroupCache, clearAllClientCache } from '../utils/devCache'
import weatherStationIcon from '../assets/weather-station.svg'
import groupIcon from '../assets/icons/group.svg'
import meteogramIcon from '../assets/icons/meteogram.svg'
import forecastIcon from '../assets/icons/forecast.svg'
import { changelogApi } from '../api/changelogApi'
import { ChangelogModal } from '../components/ChangelogModal'

// localStorage klíč: datum nejnovějšího záznamu logu změn, který uživatel už otevřel.
const CHANGELOG_SEEN_KEY = 'cw_changelog_seen'

// Tlačítka v pravém horním rohu (Návod, Log změn) — sdílený vzhled.
const TOP_RIGHT_BUTTON_CLASS = 'flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors text-xs sm:text-sm text-gray-700'

// Ikony přicházejí v různých skutečných rozměrech (emoji vs. SVG v různě velkých <img>) —
// bez pevného slotu stejné výšky by "justify-center" v tlačítku posouvalo text do jiné
// pozice podle toho, jak vysoká ikona zrovna přišla (viditelné např. mezi "Grafy stanic" -
// emoji - a "Meteogramy Aladin" - velké SVG). Ikonový slot (ICON_BOX_CLASS, sdílené i s
// jednotlivými <img> ikonami níže) má proto vždy stejnou výšku a obsah tlačítka je
// zarovnaný nahoru (justify-start) — ikony i začátek textu jsou tak u všech karet na
// stejné pozici; jednořádkový label nechá zbytek pevné výšky tlačítka jako volný prostor
// dole, víceřádkový ho přirozeně vyplní.
const ICON_BOX_CLASS = 'w-12 h-12 sm:w-14 sm:h-14'

function IconCard({ icon, label, onClick, children }) {
  return (
    <div className="flex flex-col items-center gap-3 w-full">
      <button
        onClick={onClick}
        className="flex flex-col items-center gap-2 p-4 rounded-2xl border border-gray-200 bg-white hover:bg-blue-50 hover:border-blue-300 hover:shadow-md transition-all w-full max-w-52 h-32 sm:h-36 justify-start"
      >
        <div className={`${ICON_BOX_CLASS} flex items-center justify-center shrink-0 text-4xl sm:text-5xl leading-none`}>
          {icon}
        </div>
        <span className="font-semibold text-gray-700 text-sm sm:text-base text-center leading-tight">{label}</span>
      </button>
      {children && (
        <div className="flex flex-col gap-1 w-full max-w-52">
          {children}
        </div>
      )}
    </div>
  )
}

function SubItem({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-left text-sm text-blue-600 hover:text-blue-800 hover:underline px-2 py-0.5 rounded transition-colors truncate"
    >
      {label}
    </button>
  )
}

// Tlačítko v sekci Administrace — spustí akci, krátce ukáže výsledek
function AdminButton({ label, hint, onRun }) {
  const [state, setState] = useState(null) // null | 'running' | 'done' | 'error'
  const [msg, setMsg] = useState('')

  const run = async () => {
    setState('running')
    try {
      const result = await onRun()
      setMsg(typeof result === 'string' ? result : 'Hotovo')
      setState('done')
    } catch (e) {
      setMsg(e?.message || 'Chyba')
      setState('error')
    }
    setTimeout(() => setState(null), 2500)
  }

  return (
    <button
      onClick={run}
      disabled={state === 'running'}
      title={hint}
      className={`text-left text-sm px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-60 ${
        state === 'done' ? 'border-green-300 bg-green-50 text-green-700'
        : state === 'error' ? 'border-red-300 bg-red-50 text-red-700'
        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300'
      }`}
    >
      {state === 'done' || state === 'error' ? msg : state === 'running' ? 'Pracuji…' : label}
    </button>
  )
}

export function LayoutMain() {
  const navigate     = useNavigate()
  const lastStations = getLastStations()
  const lastMeteogramPoints = getLastMeteogramPoints()
  const lastStationChartsStations = getLastStationChartsStations()
  const lastForecastSelection = getLastForecastSelection()
  const user         = useAuthStore(s => s.user)
  const isAdmin      = user?.is_admin === 'Y'
  const savedGroups  = useSavedStationGroups()
  const menuIcons    = useDeployConfigStore(s => s.menuIcons)
  const configLoaded = useDeployConfigStore(s => s.loaded)
  const variant       = useDeployConfigStore(s => s.variant)
  const footerNote    = useDeployConfigStore(s => s.footerNote)

  // Návod je per-varianta soubor (help_A.pdf/help_B.pdf/help_C.pdf v public/) — na cílovém
  // stroji nemusí existovat pro každou variantu, proto se ověří HEAD requestem a odkaz se
  // zobrazí jen když soubor skutečně existuje (žádný mrtvý odkaz na hlavní stránce).
  const helpUrl = `/help_${variant}.pdf`
  const [helpAvailable, setHelpAvailable] = useState(false)
  useEffect(() => {
    if (!variant) return
    let cancelled = false
    fetch(helpUrl, { method: 'HEAD' })
      // Chybějící statický soubor spadne v dev/SPA fallbacku zpět na index.html (200 OK,
      // text/html) místo 404 — ok samo o sobě tedy nestačí, ověřit i content-type PDF.
      .then(res => { if (!cancelled) setHelpAvailable(res.ok && (res.headers.get('content-type') ?? '').includes('pdf')) })
      .catch(() => { if (!cancelled) setHelpAvailable(false) })
    return () => { cancelled = true }
  }, [helpUrl, variant])

  // Log změn (GET /api/changelog) — tlačítko pod Návodem, jen když server vrátí aspoň
  // jeden záznam. Tečka „nové“ svítí, dokud uživatel neotevře log s nejnovějším datem;
  // naposledy viděné datum se drží v localStorage (per prohlížeč, ne per účet).
  // Anonymnímu uživateli se log změn nezobrazuje ani nestahuje.
  const isLoggedIn = !!user
  const [changelog, setChangelog] = useState([])
  const [changelogOpen, setChangelogOpen] = useState(false)
  const [changelogSeen, setChangelogSeen] = useState(() => {
    try { return localStorage.getItem(CHANGELOG_SEEN_KEY) } catch { return null }
  })
  useEffect(() => {
    if (!isLoggedIn) {
      setChangelog([])
      setChangelogOpen(false)
      return
    }
    let cancelled = false
    changelogApi.getChangelog()
      .then(data => { if (!cancelled && Array.isArray(data)) setChangelog(data) })
      .catch(() => { /* log změn je doplněk — při chybě se tlačítko prostě neukáže */ })
    return () => { cancelled = true }
  }, [isLoggedIn])
  const latestChangelogDate = changelog[0]?.date ?? null
  const changelogHasNew = latestChangelogDate !== null && (changelogSeen === null || latestChangelogDate > changelogSeen)
  const openChangelog = () => {
    setChangelogOpen(true)
    if (!latestChangelogDate) return
    setChangelogSeen(latestChangelogDate)
    try { localStorage.setItem(CHANGELOG_SEEN_KEY, latestChangelogDate) } catch { /* úložiště nedostupné */ }
  }

  // Pořadí ikon v gridu odpovídá pořadí, v jakém je server pošle v menuIcons — ne pevnému
  // pořadí JSX bloků (dřívější chování ignorovalo pořadí z configu). Admin ikony nejsou
  // součástí configu (správa je vždy jen pro adminy, napříč variantami) a řadí se za ně.
  const ICON_RENDERERS = {
    'station-simple': () => (
      <IconCard
        key="station-simple"
        icon={
          <span className={`flex items-center justify-center ${ICON_BOX_CLASS} rounded-full border-2 bg-blue-50 text-blue-600`} style={{ borderColor: '#2F88FF' }}>
            <img src={weatherStationIcon} className="w-4/5 h-4/5" alt="" />
          </span>
        }
        label="Jedna stanice"
        onClick={() => navigate('/select')}
      >
        {lastStations.length > 0 && (
          <>
            <p className="text-xs text-gray-400 px-2">Poslední stanice:</p>
            {lastStations.map(s => (
              <SubItem
                key={s.gh_id}
                label={`${s.gh_id} – ${s.name}`}
                onClick={() => navigate(`/station/${s.gh_id}`)}
              />
            ))}
          </>
        )}
      </IconCard>
    ),
    'station-group': () => (
      <IconCard
        key="station-group"
        icon={<img src={groupIcon} className={`${ICON_BOX_CLASS} object-contain`} alt="" />}
        label="Skupina stanic"
        onClick={() => navigate('/station-group')}
      >
        {savedGroups.map(g => (
          <SubItem
            key={g.id}
            label={g.name}
            onClick={() => navigate('/station-group', { state: { savedGroupId: g.id } })}
          />
        ))}
      </IconCard>
    ),
    meteogram: () => (
      <IconCard
        key="meteogram"
        icon={<img src={meteogramIcon} className={`${ICON_BOX_CLASS} object-contain`} alt="" />}
        label="Meteogramy Aladin"
        onClick={() => navigate('/meteogram')}
      >
        {lastMeteogramPoints.length > 0 && (
          <>
            <p className="text-xs text-gray-400 px-2">Poslední místa:</p>
            {lastMeteogramPoints.map(p => (
              <SubItem
                key={p.id}
                label={p.extra ? `${p.name} (${p.extra})` : p.name}
                onClick={() => navigate('/meteogram', { state: { pointId: p.id } })}
              />
            ))}
          </>
        )}
      </IconCard>
    ),
    // Rychlé odkazy: ČR + poslední zvolený kraj/pohoří (pokud si je uživatel na stránce
    // Předpovědi už někdy vybral).
    forecast: () => (
      <IconCard
        key="forecast"
        icon={<img src={forecastIcon} className={`${ICON_BOX_CLASS} object-contain`} alt="" />}
        label="Předpovědi"
        onClick={() => navigate('/forecast', { state: { category: 'CR' } })}
      >
        <SubItem label="ČR" onClick={() => navigate('/forecast', { state: { category: 'CR' } })} />
        {lastForecastSelection.regionByCategory.Kraje && (
          <SubItem
            label={lastForecastSelection.regionByCategory.Kraje.name || 'Kraj'}
            onClick={() => navigate('/forecast', {
              state: { category: 'Kraje', region: lastForecastSelection.regionByCategory.Kraje.region },
            })}
          />
        )}
        {lastForecastSelection.regionByCategory.Hory && (
          <SubItem
            label={lastForecastSelection.regionByCategory.Hory.name || 'Pohoří'}
            onClick={() => navigate('/forecast', {
              state: { category: 'Hory', region: lastForecastSelection.regionByCategory.Hory.region },
            })}
          />
        )}
      </IconCard>
    ),
    'station-charts': () => (
      <IconCard
        key="station-charts"
        icon="📈"
        label="Grafy stanic"
        onClick={() => navigate('/station-charts')}
      >
        {lastStationChartsStations.length > 0 && (
          <>
            <p className="text-xs text-gray-400 px-2">Poslední stanice:</p>
            {lastStationChartsStations.map(s => (
              <SubItem
                key={s.gh_id}
                label={`${s.gh_id} – ${s.name}`}
                onClick={() => navigate(`/station-charts/${s.gh_id}`)}
              />
            ))}
          </>
        )}
      </IconCard>
    ),
    settings: () => (
      <IconCard key="settings" icon="⚙️" label="Nastavení" onClick={() => navigate('/settings/password')} />
    ),
  }

  const menuIconCards = menuIcons.map(id => ICON_RENDERERS[id]?.()).filter(Boolean)

  // Správa skupin stanic nemá smysl nabízet, když je panel Skupina stanic (station-group)
  // na téhle instanci schovaný z menuIcons úplně — nedávalo by smysl spravovat skupiny,
  // které tu žádný uživatel (ani admin skrz normální menu) nemůže vůbec zobrazit.
  const adminCards = isAdmin ? [
    <IconCard key="admin-users" icon="👥" label="Správa uživatelů" onClick={() => navigate('/admin/users')} />,
    menuIcons.includes('station-group') && (
      <IconCard
        key="admin-station-groups"
        icon={
          <span className={`flex items-center justify-center gap-0.5 ${ICON_BOX_CLASS}`}>
            <img src={groupIcon} className="w-3/5 h-3/5 object-contain" alt="" />
            <span className="text-2xl sm:text-3xl">🛠️</span>
          </span>
        }
        label="Správa skupin stanic"
        onClick={() => navigate('/admin/station-groups')}
      />
    ),
    <IconCard key="admin-scheduler" icon="⏱️" label="Naplánované úlohy" onClick={() => navigate('/admin/scheduler')} />,
  ].filter(Boolean) : []

  const allCards = [...menuIconCards, ...adminCards]
  const visibleCount = allCards.length
  const gridColsClass = visibleCount > 4 ? 'grid-cols-2 md:grid-cols-6'
    : visibleCount > 2 ? 'grid-cols-2 md:grid-cols-3'
    : 'grid-cols-2'
  const gridMaxWClass = visibleCount > 4 ? 'max-w-7xl' : visibleCount > 2 ? 'max-w-3xl' : 'max-w-xl'

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 sm:p-8 gap-4 sm:gap-8">
      {/* UserIcon — levý horní roh */}
      <div className="fixed top-2 left-2 sm:top-4 sm:left-4 z-40">
        <UserIcon />
      </div>

      {/* Pravý horní roh: Návod (jen když existuje soubor pro aktuální variantu nasazení)
          a pod ním Log změn (jen přihlášenému uživateli a jen když server vrátí záznamy) */}
      <div className="fixed top-2 right-2 sm:top-4 sm:right-4 z-40 flex flex-col items-stretch gap-1.5 sm:gap-2">
        {helpAvailable && (
          <a
            href={helpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={TOP_RIGHT_BUTTON_CLASS}
            title="Otevřít návod (PDF)"
          >
            <span className="text-base sm:text-lg">📖</span>
            <span className="hidden sm:inline">Návod</span>
          </a>
        )}
        {isLoggedIn && changelog.length > 0 && (
          <button
            onClick={openChangelog}
            className={`relative ${TOP_RIGHT_BUTTON_CLASS}`}
            title={changelogHasNew ? 'Log změn — jsou nové změny' : 'Log změn'}
          >
            <span className="text-base sm:text-lg">📝</span>
            <span className="hidden sm:inline">Log změn</span>
            {changelogHasNew && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white" />
            )}
          </button>
        )}
      </div>

      {changelogOpen && (
        <ChangelogModal entries={changelog} onClose={() => setChangelogOpen(false)} />
      )}

      <div className="text-center mt-10 sm:mt-0">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Klimatologický web</h1>
        <p className="text-gray-400 mt-1 text-sm sm:text-base">Vyberte požadovanou sekci</p>
      </div>

      {!configLoaded ? (
        <div className="flex items-center justify-center h-52">
          <p className="text-gray-400 text-sm">Načítám…</p>
        </div>
      ) : (
        <div className={`grid gap-3 sm:gap-6 w-full px-2 justify-items-stretch ${gridMaxWClass} ${gridColsClass}`}>
          {allCards}
        </div>
      )}

      {/* Poznámka o zdroji dat pro dané nasazení — pod ikonami, v běžném toku layoutu
          (dřív `fixed bottom`, to se ale na nižších obrazovkách/delším textu překrývalo
          s poslední řadou ikon nebo jejich podnabídkou) */}
      {footerNote && (
        <p className="text-xs text-gray-400 text-center max-w-xl px-2">
          {footerNote}
        </p>
      )}

      {/* Sekce Administrace — jen pro adminy */}
      {isAdmin && (
        <div className="w-full max-w-2xl border border-gray-200 rounded-2xl bg-white p-5">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Administrace</h2>
            <span className="text-xs text-gray-400">Varianta nasazení: {variant}</span>
          </div>
          <p className="text-xs text-gray-400 mb-3">Vyčištění serverových i klientských cache (data se znovu načtou z databáze)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <AdminButton
              label="🗑️ Cache skupin"
              hint="Server: station_group* + saved_station_group. Klient: React Query dotazy skupin + stromy v IndexedDB (window.clearGroupCache)"
              onRun={async () => {
                const { diskFiles, trees } = await clearGroupCache()
                return `Smazáno (${diskFiles} souborů, ${trees} stromů)`
              }}
            />
            <AdminButton
              label="🗑️ Cache stanic"
              hint="Server: geography, station_hierarchy, station_owner, observation. Klient: allStations + hierarchie (window.clearStationCache)"
              onRun={async () => {
                const n = await clearStationCache()
                return `Cache stanic smazána (${n} souborů)`
              }}
            />
            <AdminButton
              label="🗑️ Cache číselníků"
              hint="Server + klient: veličiny, typy stanic, obs_type, povodí (window.clearCodebookCache)"
              onRun={async () => {
                const n = await clearCodebookCache()
                return `Číselníky smazány (${n} souborů)`
              }}
            />
            <AdminButton
              label="🗑️ Všechny cache"
              hint="Kompletní reset: všechny serverové cache (memory + offline soubory) + React Query + IndexedDB (window.clearAllClientCache)"
              onRun={async () => { await clearAllClientCache(); return 'Vše smazáno' }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
